import { useEffect, useRef, useCallback } from "react";
import { MinimalEstateDocument } from "src/types/snugdocs";
import { GenericPath, useV3GenericGetData } from "src/api/snugtotal/estate_app";
import toast from "react-hot-toast";

interface UseEstateDocumentPollingProps {
  estateDocuments: Array<{ doc: MinimalEstateDocument; owner_ud_id: string }>;
  onDocumentUpdated: (updatedDoc: MinimalEstateDocument) => void;
  pollingInterval?: number; // milliseconds, default 3000
}

/**
 * Polls estate documents with PENDING status and notifies when they complete.
 * Automatically stops polling when no more PENDING documents exist.
 *
 * @param estateDocuments - Array of estate documents with their owner's ud_id to monitor
 * @param onDocumentUpdated - Callback when a document status changes from PENDING
 * @param pollingInterval - How often to poll in milliseconds (default: 3000ms)
 */
export const useEstateDocumentPolling = ({
  estateDocuments,
  onDocumentUpdated,
  pollingInterval = 3000,
}: UseEstateDocumentPollingProps) => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const getSingleEstateDocument = useV3GenericGetData<MinimalEstateDocument>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );

  // Find all PENDING or REVIEWABLE documents (treat REVIEWABLE as still generating)
  const pendingDocs = estateDocuments.filter(
    (item) => item.doc.status === "PENDING" || item.doc.status === "REVIEWABLE"
  );

  const pollDocument = useCallback(
    async (docId: string, ownerUdId: string) => {
      if (!ownerUdId) return;

      try {
        const updatedDoc = await getSingleEstateDocument({
          ud_id: ownerUdId,
          entity: "estate-documents",
          entity_id: docId,
        });

        // Check if status changed from PENDING/REVIEWABLE to something else (APPROVED or other final states)
        if (updatedDoc.status !== "PENDING" && updatedDoc.status !== "REVIEWABLE") {
          // Find the document type for the toast message
          const docTypeDisplay =
            updatedDoc.type === "WILL"
              ? "Will"
              : updatedDoc.type === "TRUST"
              ? "Trust"
              : updatedDoc.type === "FINANCIAL_POWER_OF_ATTORNEY"
              ? "Financial Power of Attorney"
              : updatedDoc.type === "HEALTHCARE_DIRECTIVE"
              ? "Healthcare Directive"
              : "Document";

          // Show success toast
          if (updatedDoc.status === "APPROVED") {
            toast.success(`Your ${docTypeDisplay} is ready!`);
          }

          // Notify parent component
          onDocumentUpdated(updatedDoc);
        }
      } catch (error) {
        console.error("Error polling estate document:", error);
        // Don't show error toast - just log it and continue polling
      }
    },
    [getSingleEstateDocument, onDocumentUpdated]
  );

  const pollAllPendingDocuments = useCallback(async () => {
    if (pendingDocs.length === 0) {
      // No pending documents, stop polling
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Poll each pending document with its owner's ud_id
    await Promise.all(
      pendingDocs.map((item) => pollDocument(item.doc.id || "", item.owner_ud_id))
    );
  }, [pendingDocs, pollDocument]);

  useEffect(() => {
    // Start polling if there are pending documents
    if (pendingDocs.length > 0 && !intervalRef.current) {
      // Poll immediately on mount
      pollAllPendingDocuments();

      // Then set up interval
      intervalRef.current = setInterval(
        pollAllPendingDocuments,
        pollingInterval
      );
    }

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [pendingDocs.length, pollingInterval, pollAllPendingDocuments]);

  return {
    isPolling: pendingDocs.length > 0,
    pendingCount: pendingDocs.length,
  };
};
